import { isEmpty, mapValues } from 'lodash';

import { ApiError } from '@sonar/errors';
import { Period, Reference, TimeSlot } from '@sonar/models';

import {
  computeAggregatedOrdersEvolution,
  initAggregatedOrder,
  sumAggregatedOrders,
} from './helpers';
import { AggregatedOrderInfo } from './types';
import useOrdersQuery from './useOrdersQuery';

interface UseOrdersKpiParams {
  storeIds: number[];
  period: Period;
  timeSlot: TimeSlot;
  referenceCode?: Reference;
  isTrainingMode?: boolean;
  // TODO: (#ORE-1467) Remove this prop when all users are migrated to new reports
  canRequestReports?: boolean;
}

interface UseOrdersKpiResult {
  isLoading: boolean;
  isError: boolean;
  error?: ApiError;
  selected: AggregatedOrderInfo;
  reference?: AggregatedOrderInfo;
  evolution: AggregatedOrderInfo;
  kpisPerStore: {
    [storeId: number]: {
      selected: AggregatedOrderInfo;
      reference?: AggregatedOrderInfo;
      evolution: AggregatedOrderInfo;
    };
  };
}

function useOrdersKpi({
  storeIds,
  period,
  timeSlot,
  referenceCode,
  isTrainingMode = false,
  canRequestReports = true,
}: UseOrdersKpiParams): UseOrdersKpiResult {
  const {
    data = [],
    error,
    isLoading,
    isError,
  } = useOrdersQuery({
    storeIds,
    period,
    timeSlot,
    isTrainingMode,
    referenceCode,
    canRequestReports,
  });

  const selectedOrdersKpis = mapValues(data[0] || {}, ([aggOrders]) => aggOrders);
  const referenceOrdersKpis = mapValues(data[1] || {}, ([aggOrders]) => aggOrders);

  const kpisPerStore: {
    [storeId: number]: {
      selected: AggregatedOrderInfo;
      reference?: AggregatedOrderInfo;
      evolution: AggregatedOrderInfo;
    };
  } = {};
  let selectedKpis = initAggregatedOrder();
  let referenceKpis = initAggregatedOrder();

  storeIds.forEach(storeId => {
    selectedKpis = sumAggregatedOrders(selectedKpis, selectedOrdersKpis[storeId]);
    referenceKpis = sumAggregatedOrders(referenceKpis, referenceOrdersKpis[storeId]);

    kpisPerStore[storeId] = {
      selected: selectedOrdersKpis[storeId] || initAggregatedOrder(),
      reference: referenceOrdersKpis[storeId],
      evolution: computeAggregatedOrdersEvolution(
        selectedOrdersKpis[storeId],
        referenceOrdersKpis[storeId],
      ),
    };
  }, {});

  const evolutionKpis = computeAggregatedOrdersEvolution(selectedKpis, referenceKpis);

  return {
    isLoading,
    isError,
    error: error ?? undefined,
    selected: selectedKpis,
    reference: !isEmpty(referenceOrdersKpis) ? referenceKpis : undefined,
    evolution: evolutionKpis,
    kpisPerStore,
  };
}

export default useOrdersKpi;
